<script setup lang="ts">
/**
 * Checkbox — used for table row selection and individual form fields.
 *
 * Tri-state aware (true | false | 'indeterminate') for the table
 * select-all-with-partial pattern.
 */
import { Check, Minus } from 'lucide-vue-next'

interface Props {
  modelValue: boolean | 'indeterminate'
  disabled?: boolean
  id?: string
}

const props = defineProps<Props>()

defineEmits<{
  'update:modelValue': [value: boolean | 'indeterminate']
}>()

const fieldId =
  props.id ??
  `checkbox-${Math.random().toString(36).slice(2, 10)}`
</script>

<template>
  <button
    :id="fieldId"
    role="checkbox"
    type="button"
    :aria-checked="(modelValue === 'indeterminate' ? 'mixed' : modelValue) as boolean | 'mixed'"
    :disabled="disabled || undefined"
    :data-state="modelValue === 'indeterminate' ? 'indeterminate' : modelValue ? 'checked' : 'unchecked'"
    class="flex h-4 w-4 shrink-0 items-center justify-center
           rounded-sm border transition-colors
           data-[state=checked]:bg-brand data-[state=checked]:border-brand
           data-[state=indeterminate]:bg-brand data-[state=indeterminate]:border-brand
           data-[state=unchecked]:bg-surface-2 data-[state=unchecked]:border-border
           hover:border-border-strong
           focus-visible:outline-none focus-visible:shadow-focus
           disabled:cursor-not-allowed disabled:opacity-50"
    @click="$emit('update:modelValue', modelValue === true ? false : modelValue === 'indeterminate' ? false : true)"
    @keydown.space.prevent="$emit('update:modelValue', modelValue === true ? false : modelValue === 'indeterminate' ? false : true)"
  >
    <Check
      v-if="modelValue === true"
      :size="11"
      :stroke-width="3"
      class="text-text-1"
    />
    <Minus
      v-else-if="modelValue === 'indeterminate'"
      :size="11"
      :stroke-width="3"
      class="text-text-1"
    />
  </button>
</template>
